import { IUser } from '@vivli/shared/infrastructure/interface';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { first } from 'rxjs/operators';
import ReactGA from 'react-ga4';
import { PermissionsEnum } from '@vivli/shared/infrastructure/enum';
import { IUsersApi } from '@vivli/features/users/infrastructure/interface';
import { IActiveUser } from '@vivli/core/infrastructure/interface';
import { Subscription } from 'rxjs';
import { useAppConfigContext, useAuthEventsContext, useAuthServiceContext } from '@vivli/core/infrastructure/context';

const getClaimsFromIdToken = (idToken: string): any => {
    if (!idToken) return null;

    try {
        return JSON.parse(atob(idToken.split('.')[1]));
    } catch (error) {
        return null;
    }
};

interface IUserContext {
    usersApi: IUsersApi;
}

export const useActiveUser = ({ usersApi }: IUserContext): IActiveUser => {
    const config = useAppConfigContext();
    const { getTokenSilent } = useAuthServiceContext();
    const { getUserOnLogin } = usersApi;
    const authEvents = useAuthEventsContext();

    const [user, setUser] = useState<IUser>(null);
    const authSub = useRef<Subscription>();
    const setupUserInProgressRef = useRef(false);

    const userHasAnyPermission = useCallback((userIn: IUser, permissions: PermissionsEnum[]) => {
        return (
            (userIn?.vivliPermissions?.some((p) => permissions?.includes(p)) ||
                userIn?.orgMemberships?.some((om) => om.isEnabled && om.vivliPermissions?.some((pr) => permissions?.includes(pr)))) ??
            false
        );
    }, []);

    const assignUserParams = useCallback(
        (userToAssign: IUser) => {
            //TODO PERMFE - redo with permissions+
            // userToAssign.isAnnotator = userToAssign?.orgMemberships?.some((uom) => om.isAnnotator);
            // userToAssign.isQaReviewer = userToAssign?.orgMemberships?.some((om) => om.isQaReviewer);
            userToAssign.isAnnotator = userHasAnyPermission(userToAssign, [PermissionsEnum.CurateStudy]);
            userToAssign.isQaReviewer = userHasAnyPermission(userToAssign, [PermissionsEnum.QaCuration]);
        },
        [userHasAnyPermission]
    );

    const userHasPermission = useCallback((userIn: IUser, permission: PermissionsEnum) => {
        return userIn?.vivliPermissions?.includes(permission) ?? false;
    }, []);

    const hasPermission = useCallback(
        (permission: PermissionsEnum) => {
            return userHasPermission(user, permission);
        },
        [user, userHasPermission]
    );

    const userOrgHasPermission = useCallback((userIn: IUser, orgId: string, permission: PermissionsEnum) => {
        return (
            userIn?.orgMemberships?.some((org) => org.isEnabled && org.orgId == orgId && org.vivliPermissions.includes(permission)) ?? false
        );
    }, []);

    const orgHasPermission = useCallback(
        (orgId: string, permission: PermissionsEnum) => {
            return userOrgHasPermission(user, orgId, permission);
        },
        [user, userOrgHasPermission]
    );

    const hasAnyPermission = useCallback(
        (permissions: PermissionsEnum[] | PermissionsEnum) => {
            const _permissions = Array.isArray(permissions) ? permissions : [permissions];
            return userHasAnyPermission(user, _permissions);
        },
        [user, userHasAnyPermission]
    );

    const hasAllPermissions = useCallback(
        (permissions: PermissionsEnum[]) => {
            return permissions.every(hasAnyPermission);
        },
        [hasAnyPermission]
    );

    const userHasAnyPermissionForOrg = useCallback((userIn: IUser, orgId: string, permissions: PermissionsEnum[]) => {
        return (
            userIn?.orgMemberships?.some(
                (om) => om.isEnabled && om.orgId === orgId && om.vivliPermissions?.some((pr) => permissions?.includes(pr))
            ) ?? false
        );
    }, []);

    const hasAnyPermissionForOrg = useCallback(
        (orgId: string, permissions: PermissionsEnum[]) => {
            return userHasAnyPermissionForOrg(user, orgId, permissions);
        },
        [user, userHasAnyPermissionForOrg]
    );

    //special purpose where we don't care what the org is, just if user has some permission in SOME org
    const userHasAnyPermissionForAnyOrg = useCallback((userIn: IUser, permissions: PermissionsEnum[]) => {
        return userIn?.orgMemberships?.some((om) => om.isEnabled && om.vivliPermissions?.some((pr) => permissions?.includes(pr))) ?? false;
    }, []);

    const hasAnyPermissionForAnyOrg = useCallback(
        (permissions: PermissionsEnum[]) => {
            return userHasAnyPermissionForAnyOrg(user, permissions);
        },
        [user, userHasAnyPermissionForAnyOrg]
    );

    const getUserWithRoles = useCallback(() => {
        if (setupUserInProgressRef.current) return;
        setupUserInProgressRef.current = true;

        authEvents.next('get-user-info');

        getTokenSilent().then((tokenResponse) => {
            const claims = getClaimsFromIdToken(tokenResponse);
            if (!claims) {
                authEvents.next('get-user-info-failure');
                return;
            }

            const oidClaim = claims['oid'];
            const nameClaim = claims['name'];
            const emailClaim = claims['email'];
            const expClaim = claims['exp'];
            const idpClaim = claims['idp'];

            let user: IUser;
            getUserOnLogin(oidClaim)
                .pipe(first())
                .subscribe({
                    next: (response) => {
                        user = {
                            ...response,
                            userId: oidClaim,
                            email: emailClaim,
                            expiration: expClaim,
                            name: nameClaim
                        };

                        assignUserParams(user);

                        // always set user regardless of getting roles
                        setUser(user);
                        if (config.gaMeasurementId) {
                            ReactGA.set({ userId: user.userId });
                        }

                        setupUserInProgressRef.current = false;
                        authEvents.next('get-user-info-success');
                    },
                    error: (error) => {
                        authEvents.next('get-user-info-failure');
                    }
                });
        });
    }, [config.gaMeasurementId, getUserOnLogin, assignUserParams]);

    const cleanupUser = useCallback(() => {
        setUser(null);
    }, []);

    useEffect(() => {
        if (authSub.current) {
            return;
        }

        authSub.current = authEvents.subscribe((event) => {
            switch (event) {
                case 'login-success':
                    getUserWithRoles();
                    break;
                // case MsalEventTypeEnum.UnauthorizedEvent:
                case 'logout-success':
                    cleanupUser();
                    break;
            }
        });

        return () => {
            authSub.current?.unsubscribe();
            authSub.current = null;
        };
    }, [authEvents]);

    return useMemo(
        () => ({
            user,
            hasPermission,
            hasAnyPermission,
            hasAllPermissions,
            orgHasPermission,
            hasAnyPermissionForOrg,
            hasAnyPermissionForAnyOrg,
            userHasPermission,
            userHasAnyPermission,
            userOrgHasPermission,
            userHasAnyPermissionForOrg,
            userHasAnyPermissionForAnyOrg
        }),
        [
            user,
            hasPermission,
            hasAnyPermission,
            hasAllPermissions,
            orgHasPermission,
            hasAnyPermissionForOrg,
            hasAnyPermissionForAnyOrg,
            userHasPermission,
            userHasAnyPermission,
            userOrgHasPermission,
            userHasAnyPermissionForOrg,
            userHasAnyPermissionForAnyOrg
        ]
    );
};
